import { useState, useEffect, useCallback } from 'react';
import { Shield, Truck, Clock, AlertTriangle, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Service Timer Display for Client Dashboard
const ServiceTimerCard = ({ service, type, onContinue }) => {
  const Icon = type === 'compliance' ? Shield : Truck;
  const color = type === 'compliance' ? '#C0C0C0' : '#D4AF37';
  const label = type === 'compliance' ? 'Compliance Support' : 'Dispatch Support';
  
  const isExpired = service.is_expired;
  const isPaused = service.is_paused;
  const isWarning = service.days_remaining <= 7 && service.days_remaining > 0;
  
  const progressPercent = service.total_days > 0 
    ? Math.max(0, Math.min(100, (service.days_remaining / service.total_days) * 100))
    : 0;
  
  return (
    <div className={`bg-zinc-800/30 border p-4 ${
      isExpired ? 'border-red-500/50' : isPaused ? 'border-amber-500/50' : 'border-zinc-700'
    }`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div 
            className="w-10 h-10 flex items-center justify-center"
            style={{ backgroundColor: isExpired ? '#EF444420' : `${color}20` }}
          >
            <Icon className="w-5 h-5" style={{ color: isExpired ? '#EF4444' : color }} />
          </div>
          <div>
            <h4 className="text-white font-medium">{label}</h4>
            {isPaused ? (
              <span className="text-amber-400 text-xs font-medium">PAUSED</span>
            ) : isExpired ? (
              <span className="text-red-400 text-xs font-medium">EXPIRED</span>
            ) : isWarning ? (
              <span className="text-orange-400 text-xs font-medium">EXPIRING SOON</span>
            ) : (
              <span className="text-emerald-400 text-xs font-medium">ACTIVE</span>
            )}
          </div>
        </div>
        
        <div className="text-right">
          <p className="text-2xl font-bold" style={{ 
            color: isExpired ? '#EF4444' : isPaused ? '#F59E0B' : isWarning ? '#F97316' : color 
          }}>
            {service.days_remaining}
          </p>
          <p className="text-zinc-500 text-xs">days remaining</p>
        </div>
      </div>
      
      {/* Progress bar */}
      <div className="mb-3">
        <div className="h-2 bg-zinc-700 overflow-hidden">
          <div 
            className="h-full transition-all duration-500"
            style={{ 
              width: `${progressPercent}%`,
              backgroundColor: isExpired ? '#EF4444' : isPaused ? '#F59E0B' : isWarning ? '#F97316' : color
            }}
          />
        </div>
      </div>
      
      {/* Expired state - show continue option */}
      {isExpired && (
        <button
          onClick={() => onContinue(type)}
          className="w-full px-4 py-2 bg-[#D4AF37] text-black font-medium text-sm hover:bg-[#F5D47B] flex items-center justify-center gap-2"
        >
          Continue {label} <ChevronRight className="w-4 h-4" />
        </button>
      )}
      
      {/* Warning state */}
      {isWarning && !isExpired && (
        <div className="flex items-center gap-2 text-orange-400 text-sm">
          <AlertTriangle className="w-4 h-4" />
          <span>Service expires in {service.days_remaining} days</span>
        </div>
      )}
    </div>
  );
};

// Main Authority Status Widget for Client Dashboard
const AuthorityStatusWidget = ({ onContinueService }) => {
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const fetchStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('hive_token');
      if (!token) return;
      
      const res = await axios.get(`${API}/authority/me/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch authority status:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);
  
  const handleContinue = (serviceType) => {
    // For now, show a message - later this will integrate with Stripe
    if (onContinueService) {
      onContinueService(serviceType);
    } else {
      alert(`To continue your ${serviceType} support, please contact Queen B Logistics.`);
    }
  };
  
  if (isLoading) {
    return (
      <div className="bg-zinc-900 border border-zinc-800 p-6">
        <div className="flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }
  
  if (!status) return null;
  
  // Don't show if authority not activated
  if (status.authority_status !== 'active') {
    return (
      <div className="bg-zinc-900 border border-zinc-800 p-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-500/20 flex items-center justify-center">
            <Clock className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h3 className="text-white font-bold">Authority Pending</h3>
            <p className="text-zinc-400 text-sm">
              Your service timers will begin once your authority is activated.
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-4" data-testid="authority-status-widget">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-white font-bold uppercase tracking-wider text-sm">
          Service Status
        </h3>
        <span className="text-[#D4AF37] text-sm font-medium">
          {status.tier_name} Package
        </span>
      </div>
      
      {/* Service Timers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ServiceTimerCard 
          service={status.services.compliance}
          type="compliance"
          onContinue={handleContinue}
        />
        <ServiceTimerCard 
          service={status.services.dispatch}
          type="dispatch"
          onContinue={handleContinue}
        />
      </div>
      
      {/* Both expired message */}
      {status.services.compliance.is_expired && status.services.dispatch.is_expired && (
        <div className="bg-red-500/10 border border-red-500/30 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-400 font-medium">Both services have expired</p>
              <p className="text-zinc-400 text-sm mt-1">
                Contact Queen B Logistics to continue your Compliance and Dispatch support.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { AuthorityStatusWidget, ServiceTimerCard };
export default AuthorityStatusWidget;
